/**
 * ArchiMate-weergavemodus — de omkeerbare v3.1↔v4-schakelaar.
 *
 * **Wat dit wel is**: een presentatievoorkeur per gebruiker, net als
 * `ui_language`. Het model op de server is er maar één (ArchiMate 4-typen
 * met een `archimate31.*`-compat-stereotype dat de oorspronkelijke
 * 3.1-identiteit draagt). De schakelaar kiest alleen welke van die twee
 * identiteiten je in labels, kleuren, iconen en palette te zien krijgt.
 *
 * **Wat dit niet is**: een tweede opgeslagen model. Er wordt nooit iets
 * geconverteerd bij het omzetten — daarom is wisselen kosteloos en
 * oneindig herhaalbaar.
 *
 * **Waarom een Zustand-store en geen module-singleton**: de modus stuurt de
 * render van elke node op het canvas. Een kale variabele zou 40+ nodes niet
 * opnieuw laten renderen bij het omzetten. Voor niet-React-modules (pure
 * helpers, emitters) is er `getDisplayMode()` als escape-hatch — die leest
 * dezelfde store buiten React om.
 *
 * **`packages/domain` blijft app-agnostisch**: pure functies daar krijgen de
 * modus als argument mee, ze importeren deze store niet.
 *
 * Zie `docs/architecture/archimate4-migratie.md` §4 fase 3.
 */
import { create } from "zustand";
import { api } from "../api/client";

export type DisplayMode = "v31" | "v4";

/** Server-default. Betekent: wie niets kiest ziet de vertrouwde weergave. */
export const DEFAULT_DISPLAY_MODE: DisplayMode = "v31";

export function isDisplayMode(value: unknown): value is DisplayMode {
  return value === "v31" || value === "v4";
}

interface DisplayModeState {
  mode: DisplayMode;
  /** Zet de modus lokaal zonder de server te raadplegen. Gebruikt door
   *  `session.hydrate()` om de opgeslagen voorkeur in te lezen. */
  seed: (mode: DisplayMode) => void;
  /** Zet de modus én bewaart 'm op de gebruiker. Optimistisch: de UI
   *  schakelt meteen, de PATCH loopt erachteraan. Mislukt die, dan blijft
   *  de lokale keuze staan tot de volgende hydrate — een verkeerd
   *  onthouden presentatievoorkeur is geen datarisico. */
  setMode: (mode: DisplayMode) => void;
}

export const useDisplayMode = create<DisplayModeState>((set) => ({
  mode: DEFAULT_DISPLAY_MODE,
  seed: (mode) => set({ mode }),
  setMode: (mode) => {
    set({ mode });
    void api.patchMe({ display_mode: mode }).catch(() => {
      // Bewust stil: zie de docstring hierboven.
    });
  },
}));

/**
 * Escape-hatch voor modules die geen React-component zijn (emitters,
 * pure helpers, event-handlers buiten de render-boom). Binnen componenten
 * altijd `useDisplayMode((s) => s.mode)` gebruiken — anders mist de
 * component de re-render bij het omzetten.
 */
export function getDisplayMode(): DisplayMode {
  return useDisplayMode.getState().mode;
}
